//src/types/Account.ts

import {
  collection,
  CollectionReference,
  QueryDocumentSnapshot,
  Timestamp,
  type DocumentData,
  type SnapshotOptions
} from 'firebase/firestore'
import { createAddress, createAddressWork, type Address, type AddressWork } from './Helper/Address'
import { createPilot, type Certificate, type Pilot } from './Account/Pilot'
import { convertTimestampsToDates, FirebaseProperties } from './Helper/FirebaseProperties'
import { db } from '@/firebase'
import type { DocI } from './Document'
import {
  createCurrentFinance,
  type CurrentFinance,
  type notif,
  type ObjectSummary
} from './Helper/Interfaces'
import { Roles } from './Helper/Enums'
import {
  createAdmin,
  createMembership,
  createSettings,
  createUser,
  createUserCompliance,
  type User
} from './Account/AccountInterfaces'
import type {
  Admin,
  EmergencyContact,
  MailTo,
  Membership,
  Settings
} from './Account/AccountInterfaces'
import { FirebaseCollections, FirestorePaths } from '@/utils/firestore/FirestorePaths'
import { AlertReceipt, AlertReceiptConverter } from './Account/AlertReceipt'
import { createMechanicCertificate, type MechanicCertificate } from './Account/AccountEnums'

export interface WagePaymentRecord {
  service: string
  charged: number
  paid: number
}

export type AccountStoreStripeCustomerProfile = {
  stripeCustomerId?: string | null
  stripeDefaultPaymentMethodId?: string | null
  stripePaymentMethodType?: string | null
  stripePaymentMethodBrand?: string | null
  stripePaymentMethodLast4?: string | null
  stripePaymentMethodExpMonth?: number | null
  stripePaymentMethodExpYear?: number | null
  offSessionConsentAcceptedAt?: Date | Timestamp | null
  offSessionConsentAcceptedByUid?: string | null
  offSessionConsentTextVersion?: string | null
  lastStripePaymentMethodSyncAt?: Date | Timestamp | null
}

export type AccountPayoutBillingInfo = {
  stripePayoutAccountId?: string | null
  stripePayoutTransfersEnabled?: boolean
  stripePayoutDetailsSubmitted?: boolean
  lastStripePayoutAccountSyncAt?: Date | Timestamp | null
  externalPayoutPreference?: {
    method?: 'venmo' | 'check' | 'external_ach' | 'cash' | 'other' | null
    destinationLabel?: string | null
    note?: string | null
    acknowledgedAt?: Date | Timestamp | null
    updatedAt?: Date | Timestamp | null
    updatedByUid?: string | null
  } | null
}

export type AccountBillingInfo = AccountPayoutBillingInfo & {
  stripeCustomerId?: string
  stripeDefaultPaymentMethodId?: string | null
  stripePaymentMethodType?: string | null
  stripePaymentMethodBrand?: string | null
  stripePaymentMethodLast4?: string | null
  stripePaymentMethodExpMonth?: number | null
  stripePaymentMethodExpYear?: number | null
  stripeChargeAuthorizationsByStore?: Record<
    string,
    {
      acceptedAt?: Date | Timestamp | null
      acceptedByUid?: string | null
      textVersion?: string | null
    }
  >
  lastStripePaymentMethodSyncAt?: Date | Timestamp | null
  /** Legacy store-local Stripe customers; retained only for data migration. */
  stripeCustomerProfilesByStore?: Record<string, AccountStoreStripeCustomerProfile>
  billingEmail?: string | null
  membershipSubscriptionsByStore?: Record<
    string,
    {
      stripeConnectedCustomerId?: string | null
      stripeSourcePaymentMethodId?: string | null
      stripeSubscriptionId?: string | null
      stripePriceId?: string | null
      stripeLatestInvoiceId?: string | null
      stripeHostedInvoiceUrl?: string | null
      interval?: 'month' | 'year' | null
      status?: string | null
      lastPaymentError?: string | null
      currentPeriodEnd?: Date | Timestamp | null
      updatedAt?: Date | Timestamp | null
    }
  >
}

export type AccountStoreNameInput = string | string[] | null | undefined
export type AccountStoreAdminInput =
  | Record<string, Partial<Admin> | null | undefined>
  | null
  | undefined
export type AccountInit = Omit<Partial<Account>, 'storeName' | 'storeAdmin'> & {
  storeName?: AccountStoreNameInput
  storeAdmin?: AccountStoreAdminInput
}

export function normalizeAccountStoreNames(storeName: AccountStoreNameInput): string[] {
  const values = Array.isArray(storeName) ? storeName : [storeName]

  return [
    ...new Set(
      values.map((value) => String(value ?? '').trim()).filter((value) => value.length > 0)
    )
  ]
}

export function normalizeAccountAdmin(admin: Partial<Admin> | null | undefined): Admin {
  return createAdmin({
    ...admin,
    roles: Array.isArray(admin?.roles) ? [...new Set(admin.roles)] : createAdmin().roles
  })
}

export function normalizeAccountStoreAdmin(
  storeAdmin: AccountStoreAdminInput
): Record<string, Admin> {
  const normalizedStoreAdmin: Record<string, Admin> = {}

  if (storeAdmin && typeof storeAdmin === 'object') {
    Object.entries(storeAdmin).forEach(([storeCode, admin]) => {
      const normalizedStoreCode = String(storeCode ?? '').trim()
      if (!normalizedStoreCode) return

      normalizedStoreAdmin[normalizedStoreCode] = normalizeAccountAdmin(admin)
    })
  }

  return normalizedStoreAdmin
}

/**
 * What a school with no entry for this account gives it: no roles, not active.
 * A fresh object each time, so changing it never changes the account.
 */
function noSchoolAccess(): Admin {
  return createAdmin({ roles: [], isActive: false })
}

/**
 * The school a call is about: the one given, or else the account's primary
 * school. A plain function, not a class member: a private member would make
 * Account values unwrapped by Pinia/ref() stop type-checking as Account.
 */
function schoolFor(storeName: string[], storeCode?: string): string {
  return String(storeCode ?? '').trim() || (storeName[0] ?? '')
}

export class Account extends FirebaseProperties {
  /** Transient school context, never persisted as permission data. */
  contextSchoolId = ''
  /** Navigation preference; authorization always checks the selected school's live membership. */
  _selectedSchoolId = ''
  addressHome: Address = createAddress()
  addressWork: AddressWork = createAddressWork()
  user: User = createUser()
  membership: Membership = createMembership()
  /**
   * Account-level block. Used ONLY for the database-wide Dev role — every other
   * role and flag is per school, in storeAdmin.
   */
  admin: Admin = createAdmin()
  settings: Settings = createSettings()

  mailTo: MailTo | null = null
  emergencyContact: EmergencyContact | null = null
  pilot: Pilot | null = null
  mechanic: MechanicCertificate | null = null

  finance: CurrentFinance = createCurrentFinance()

  rentersInsurance: string = ''
  rentersInsuranceExpiration: Date | null = null

  storeName: string[] = []
  /** Roles and flags per school: storeAdmin[schoolCode]. */
  storeAdmin: Record<string, Admin> = {}
  billing?: AccountBillingInfo

  signatureImage: string = ''

  documents: DocI[] = []
  notifications: notif[] = []
  wagePaymentTable: WagePaymentRecord[] = []

  //FIXME TYPES
  get financeLedgerCollection(): CollectionReference {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Finance)
    )
  }

  get logbookCollection(): CollectionReference {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Logbook)
    )
  }

  get courseCollection(): CollectionReference {
    return collection(
      db,
      FirestorePaths.nestedCollection(
        `StoresCustomers/${this.contextSchoolId || this.getPrimaryStoreName()}/Members/${this.getId()}`,
        FirebaseCollections.Courses
      )
    )
  }

  get alertReceiptsCollection(): CollectionReference<AlertReceipt> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.AlertReceipts)
    ).withConverter(AlertReceiptConverter)
  }

  get workTimeCollection(): CollectionReference {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.WorkTime)
    )
  }

  getIsMechanic(): boolean {
    return !!(this.mechanic && (this.mechanic.hasAirframe || this.mechanic.hasPowerplant))
  }

  constructor(data?: AccountInit) {
    super()
    Object.assign(this, data)
    this.user = createUser({
      ...(data?.user ?? this.user),
      compliance: createUserCompliance(data?.user?.compliance ?? this.user?.compliance)
    })
    this.storeName = normalizeAccountStoreNames(data?.storeName ?? this.storeName)
    this.admin = normalizeAccountAdmin(data?.admin ?? this.admin)
    this.storeAdmin = normalizeAccountStoreAdmin(data?.storeAdmin ?? this.storeAdmin)

    if (!Array.isArray(this.wagePaymentTable)) {
      this.wagePaymentTable = []
    }

    if (!Array.isArray(this.documents)) {
      this.documents = []
    }

    if (!Array.isArray(this.notifications)) {
      this.notifications = []
    }

    this.setId()
  }

  setId() {
    this.thisCollection = FirestorePaths.rootCollection(FirebaseCollections.Accounts)

    const id = this.firebaseInfo?.__id
    this.thisDocument = id
      ? FirestorePaths.rootDocument(FirebaseCollections.Accounts, id)
      : this.thisCollection
  }

  /** Database-wide Dev — the only role kept in the account-level block. */
  isDev(): boolean {
    return this.admin.roles.includes(Roles.Dev)
  }

  isStoreOwner(storeCode?: string): boolean {
    const school = schoolFor(this.storeName, storeCode)
    if (!school || !this.hasStore(school)) return false
    return this.storeAdmin[school]?.roles.includes(Roles.StoreOwner) ?? false
  }

  /**
   * The account's roles and flags at a school (the primary school if none is
   * given). No entry for that school → no roles, not active there.
   */
  getAdminForStore(storeCode?: string): Admin {
    const school = schoolFor(this.storeName, storeCode)
    return (school && this.storeAdmin[school]) || noSchoolAccess()
  }

  /**
   * The school's entry, created with the defaults (User, active) if missing —
   * for screens that set roles and flags on a new or newly-added member.
   */
  ensureStoreAdmin(storeCode?: string): Admin {
    const school = schoolFor(this.storeName, storeCode)
    if (!school) {
      console.warn('[Account] ensureStoreAdmin: no school for this account')
      return noSchoolAccess()
    }

    if (!this.storeAdmin[school]) {
      this.storeAdmin[school] = createAdmin()
    }

    return this.storeAdmin[school]
  }

  setAdminForStore(storeCode: string, admin: Partial<Admin>): void {
    const normalizedStoreCode = String(storeCode ?? '').trim()
    if (!normalizedStoreCode) return

    this.storeAdmin[normalizedStoreCode] = normalizeAccountAdmin(admin)
  }

  hasRole(role: Roles, storeCode?: string): boolean {
    if (role === Roles.Dev) {
      return this.isDev()
    }

    if (role === Roles.StoreOwner) {
      return this.isStoreOwner(storeCode)
    }

    return this.getAdminForStore(storeCode).roles.includes(role)
  }

  hasRoles(roles: Roles[], storeCode?: string): boolean {
    if (this.isDev() || this.isStoreOwner(storeCode)) return true
    return roles.some((role) => this.hasRole(role, storeCode))
  }

  /**
   * Makes the account a student at the school: the Student role there, and the
   * pilot section a student's training records need (a new account has none).
   */
  makeStudent(storeCode?: string) {
    this.pilot ??= createPilot()
    if (!this.hasRole(Roles.Student, storeCode)) {
      this.ensureStoreAdmin(storeCode).roles.push(Roles.Student)
    }
  }

  getSummary(): ObjectSummary {
    return {
      id: this.firebaseInfo.__id,
      name: this.getName(),
      active: this.getActive()
    }
  }

  getName(): string {
    const middle = this.user.middlename ? `${this.user.middlename} ` : ''
    return `${this.user.firstname} ${middle}${this.user.lastname}`.trim()
  }

  getId(): string {
    return this.firebaseInfo.__id
  }

  getPrimaryStoreName(): string {
    return this.storeName[0] ?? ''
  }

  hasStore(storeCode: string): boolean {
    return this.storeName.includes(storeCode)
  }

  /** Active at a school (the primary school if none is given). */
  getActive(storeCode?: string): boolean {
    return this.getAdminForStore(storeCode).isActive
  }

  getSearchables(): string {
    return [
      this.getName(),
      this.user.alias,
      this.user.email,
      this.user.cellPhone,
      this.user.phone,
      this.membership?.userCode
    ]
      .filter(Boolean)
      .join(' ')
  }

  createCopy(): Account {
    return new Account({
      ...this,
      firebaseInfo: { ...this.firebaseInfo }
    })
  }

  getAddresses(): Record<string, string> {
    if (this.mailTo) {
      return {
        Address: this.getAddressString(this.addressHome),
        'Mail to': this.getAddressString(this.mailTo)
      }
    }

    return {
      Address: this.getAddressString(this.addressHome)
    }
  }

  getIsValidToSave(): boolean {
    return !!(
      this.user.firstname &&
      this.user.lastname &&
      this.user.email &&
      this.user.cellPhone &&
      this.getAdminForStore(this.getPrimaryStoreName()).roles.length > 0 &&
      this.validatePilot()
    )
  }

  validatePilot(): boolean {
    if (!this.pilot) return true

    const { groundTow, groundTowDate, cat2, cat2Date, cat3, cat3Date, groundInsCert, ratings } =
      this.pilot

    const additionalCertsValid =
      (!groundTow || groundTowDate !== null) &&
      (!cat2 || cat2Date !== null) &&
      (!cat3 || cat3Date !== null)

    const additionalRatings: Certificate[] = ratings.flatMap((r) =>
      r
        ? [r.pilotCert, r.instructorCert].filter(
            (cert): cert is Certificate => cert !== null && cert.doi !== undefined
          )
        : []
    )

    const allCerts: Certificate[] = [...additionalRatings]
    if (groundInsCert) allCerts.push(groundInsCert)

    const ratingsValid = allCerts.every((cert) => cert.doi !== null && cert.number.trim() !== '')

    return additionalCertsValid && ratingsValid
  }

  getAddressString(address: Address): string {
    return [
      address.address,
      address.address2,
      address.city ? `${address.city},` : '',
      address.state,
      address.zip,
      address.country
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
  }

  getIsInstructor(): boolean {
    if (!this.pilot) return false

    if (this.pilot.groundInsCert) return true

    return this.pilot.ratings.some((r) => r?.instructorCert != null)
  }
}

export const AccountConverter = {
  toFirestore: (account: Account) => {
    const firebaseInfo = {
      ...account.firebaseInfo,
      _updatedAt: Timestamp.now()
    }

    // No `password` field: passwords live only in Firebase Authentication.
    const data: Record<string, unknown> = {
      addressHome: account.addressHome,
      addressWork: account.addressWork,
      user: account.user,
      membership: account.membership,
      admin: account.admin,
      settings: account.settings,
      emergencyContact: account.emergencyContact,
      mailTo: account.mailTo,
      pilot: account.pilot,
      mechanic: account.mechanic,
      currentFinance: account.finance,
      rentersInsurance: account.rentersInsurance,
      rentersInsuranceExpiration: account.rentersInsuranceExpiration,
      storeName: normalizeAccountStoreNames(account.storeName),
      billing: account.billing ?? null,
      signatureImage: account.signatureImage,
      documents: account.documents,
      notifications: account.notifications,
      wagePaymentTable: account.wagePaymentTable,
      firebaseInfo
    }

    if (Object.keys(account.storeAdmin).length > 0) {
      data.storeAdmin = account.storeAdmin
    }

    return data
  },

  fromFirestore: (
    snapshot: QueryDocumentSnapshot<DocumentData, DocumentData>,
    options?: SnapshotOptions
  ) => {
    const raw = snapshot.data(options)
    const convertedData = convertTimestampsToDates(raw)

    // A leftover `password` field on old documents is ignored, never loaded.
    delete convertedData.password

    // ── Migrate old field names on ratings ───────────────────────
    // instRatings → instructorRatings, instCert → instructorCert
    if (convertedData.pilot?.ratings) {
      convertedData.pilot.ratings = convertedData.pilot.ratings.map((r: any) => {
        if (!r) return r
        const migrated = { ...r }
        if ('instRatings' in migrated && !('instructorRatings' in migrated)) {
          migrated.instructorRatings = migrated.instRatings
          delete migrated.instRatings
        }
        if ('instCert' in migrated && !('instructorCert' in migrated)) {
          migrated.instructorCert = migrated.instCert
          delete migrated.instCert
        }
        return migrated
      })
    }

    const normalized = {
      ...convertedData,
      storeName: normalizeAccountStoreNames(convertedData.storeName),
      admin: normalizeAccountAdmin(convertedData.admin),
      storeAdmin: normalizeAccountStoreAdmin(convertedData.storeAdmin),
      pilot: convertedData.pilot ? createPilot(convertedData.pilot) : null,
      mechanic: convertedData.mechanic ? createMechanicCertificate(convertedData.mechanic) : null,
      finance: {
        balance: Number(
          convertedData?.finance?.balance ?? convertedData?.currentFinance?.balance ?? 0
        )
      },
      firebaseInfo: convertedData.firebaseInfo
    }

    return new Account(normalized)
  }
}
